import { useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import type { Id } from 'react-toastify';

import { useQueryClient } from '@tanstack/react-query';
import {
  ANTD_MODAL_SELECTOR,
  COPY_TOOL_COPIED_FEEDBACK_MS,
  COPY_TOOL_OPEN_PLAN_SAVE_TIMEOUT_MS,
  TOAST_CONTAINER_SELECTOR,
} from 'constants/appConstants';
import { ACCESS_TOKEN_STRING } from 'constants/Constant';
import {
  COPY_TOOL_COPY_ERROR_MESSAGE,
  COPY_TOOL_COPY_PARTIAL_ERROR_MESSAGE,
  COPY_TOOL_COPY_PENDING_MESSAGE,
  COPY_TOOL_COPY_SUCCESS_MESSAGE,
  COPY_TOOL_OPEN_PLAN_SAVE_ERROR_MESSAGE,
  COPY_TOOL_OPEN_PLAN_SAVE_TIMEOUT_REASON,
} from 'constants/messageConstants';
import { INPUT_BY_ID_QUERY_KEY, INPUTS_QUERY_KEY } from 'constants/queryKeys';
import { useLeavePlanAfterSaving } from 'context/PlanLeaveGuardContext';
import { useFetchGAreaUnits } from 'hooks/useFetchGAreaUnits';
import useFetchUserProfile from 'hooks/useFetchUserProfile';
import {
  ICopyPaddockDataRequest,
  ICopyTarget,
} from 'interfaces/ICopyPaddockData';
import { GUnit } from 'interfaces/IGlobalCatalogue';
import { IInput } from 'interfaces/IInput';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import BrowserStorageUtils from 'utils/BrowserStorageUtils';
import { toast } from 'utils/toast';

import { getIsCopyRunning } from '../../redux/selectors/CopyToolSelectors';
import { selectActiveGOrgId } from '../../redux/selectors/GlobalCatalogueSelectors';
import { getSelectedInputId } from '../../redux/selectors/InputSelectors';
import {
  clearAllCopied,
  clearCopied,
  copyFinished,
  copyStarted,
  finishCopyingInput,
  markCopied,
  startCopyingInputs,
} from '../../redux/slices/CopyToolSlice';
import { reloadInputDraft } from '../../redux/slices/InputSlice';
import { RootState } from '../../redux/Store';

import { getExistingTargetId } from './buildCopiedInput';
import {
  copyPaddockData,
  CopyPaddockDataContext,
  CopyPaddockDataResult,
} from './copyPaddockData';
import {
  CopyProgressRow,
  CopyRowStatus,
  renderCloseButton,
  renderCopyProgress,
} from './CopyProgressToast';

const EMPTY_G_UNITS: GUnit[] = [];

/** The open plan's changes couldn't be saved, so nothing was copied; the message says what to fix. */
export class CopyOpenPlanSaveError extends Error {}

export type CopyLookups = Pick<
  CopyPaddockDataContext,
  'source' | 'paddocksById' | 'zonesById'
>;

/** Names a destination in the copy's toast, as it was before the copy. */
export type CopyTargetLabeller = (target: ICopyTarget) => string;

// Tells each tick apart, so an older copy's clear leaves a newer copy's tick.
let lastCopiedStamp = 0;

// The ticks' pending clears, cancelled with the ticks when the signed-in user changes.
const tickClearTimers = new Set<ReturnType<typeof setTimeout>>();

// The last copy's open plan save error; kept per page, so a reopened tool still closes it.
let lastSaveErrorToastId: Id | null = null;

// The signed-in user, from the access token: the profile can drop out of the cache, the token only goes on sign-out.
const readSignedInUser = (): string | null => {
  const token = Cookies.get(ACCESS_TOKEN_STRING);
  if (!token) return null;
  try {
    return jwtDecode<{ sub?: string }>(token).sub ?? null;
  } catch {
    return null;
  }
};

// A plan field with focus holds an edit not yet in the draft; leaving it commits the edit. True when it did.
const blurFocusedPlanField = (): boolean => {
  const focused = document.activeElement;
  if (!(focused instanceof HTMLElement) || focused === document.body) {
    return false;
  }
  if (focused.closest(`${TOAST_CONTAINER_SELECTOR}, ${ANTD_MODAL_SELECTOR}`)) {
    return false;
  }
  focused.blur();
  return true;
};

/**
 * Starts a copy for the copy tool, which runs on after the tool closes. When
 * the plan open in Planning is the source or a destination, its changes are
 * saved first, so the copy reads and overwrites its latest data; if that save
 * fails, nothing is copied. Each saved document is written into the org's
 * inputs cache and its own query, its stored unsaved edits are dropped (they
 * predate the copy), the open plan's draft reloads, so its next autosave can't
 * put the old data back, and the tree ticks its paddock for a moment. One
 * toast shows the copy's progress and outcome, with a retry of the failed
 * destinations only; one copy runs at a time. Resolves once the open plan
 * is saved and, when it's a destination, its own copy has landed, so the tool
 * can hold Planning until then, with false when the open plan couldn't be
 * saved (nothing was copied, so the tool stays open). Planning is also held
 * while a running copy still has to write the open plan.
 */
export const useCopyPaddockData = (orgId: string | null) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  // Read when a copy runs rather than at render, as a retry runs after the tool has gone.
  const store = useStore<RootState>();
  const leavePlanAfterSaving = useLeavePlanAfterSaving();
  const gOrgId = useSelector(selectActiveGOrgId);
  const { data: userProfile } = useFetchUserProfile();
  const { data: gUnits = EMPTY_G_UNITS } = useFetchGAreaUnits(gOrgId);

  // Resolves once Planning has saved, or at once when Planning isn't open; gives up if Planning never answers.
  const saveOpenPlan = useCallback(
    () =>
      new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(
          () =>
            reject(
              new CopyOpenPlanSaveError(COPY_TOOL_OPEN_PLAN_SAVE_TIMEOUT_REASON)
            ),
          COPY_TOOL_OPEN_PLAN_SAVE_TIMEOUT_MS
        );
        const isAsked = leavePlanAfterSaving(
          () => {
            clearTimeout(timeout);
            resolve();
          },
          (reason) => {
            clearTimeout(timeout);
            reject(new CopyOpenPlanSaveError(reason));
          }
        );
        if (!isAsked) {
          clearTimeout(timeout);
          resolve();
        }
      }),
    [leavePlanAfterSaving]
  );

  const onSaved = useCallback(
    (saved: IInput, target: ICopyTarget) => {
      // Cleared first, so the draft reload below can't load the old edits.
      BrowserStorageUtils.removeEditedInput(saved.id);
      BrowserStorageUtils.removeOriginalInput(saved.id);
      queryClient.setQueryData<IInput[]>(
        [INPUTS_QUERY_KEY, orgId],
        (current) => {
          if (!current) return current;
          return current.some((input) => input.id === saved.id)
            ? current.map((input) => (input.id === saved.id ? saved : input))
            : [...current, saved];
        }
      );
      queryClient.setQueryData([INPUT_BY_ID_QUERY_KEY, saved.id], saved);
      if (saved.id === getSelectedInputId(store.getState())) {
        dispatch(reloadInputDraft());
      }
      lastCopiedStamp += 1;
      const marks = {
        paddockIds: [target.paddockId],
        inputIds: [saved.id],
        stamp: lastCopiedStamp,
      };
      dispatch(markCopied(marks));
      const timer = setTimeout(() => {
        tickClearTimers.delete(timer);
        dispatch(clearCopied(marks));
      }, COPY_TOOL_COPIED_FEEDBACK_MS);
      tickClearTimers.add(timer);
    },
    [dispatch, orgId, queryClient, store]
  );

  const runCopy = useCallback(
    async (
      request: ICopyPaddockDataRequest,
      lookups: CopyLookups,
      onTargetDone: (target: ICopyTarget, isCopied: boolean) => void,
      onOpenPlanSaved: () => void,
      pinnedSource: IInput | null,
      onSourceRead: (source: IInput) => void
    ): Promise<CopyPaddockDataResult> => {
      const openPlanId = getSelectedInputId(store.getState());
      const touchesOpenPlan =
        !!openPlanId &&
        (request.source.inputId === openPlanId ||
          request.destination.targets.some(
            (target) =>
              getExistingTargetId(target, request.copyMode) === openPlanId
          ));
      if (touchesOpenPlan) {
        // A task later, so the committed edit has rendered and Planning's save sees it.
        if (blurFocusedPlanField()) {
          await new Promise((resolve) => {
            setTimeout(resolve, 0);
          });
        }
        await saveOpenPlan();
      }
      onOpenPlanSaved();
      const startedUser = readSignedInUser();
      // Signed out, or in as someone else: the rest would go out as the wrong user, and land in the next user's cache.
      const isSameUser = () => {
        if (readSignedInUser() === startedUser) return true;
        // The last user's ticks go too, with their pending clears.
        tickClearTimers.forEach(clearTimeout);
        tickClearTimers.clear();
        dispatch(clearAllCopied());
        return false;
      };
      // After that save the cache holds the source's latest data; a Retry copies what the first run did.
      const source =
        pinnedSource ??
        queryClient
          .getQueryData<IInput[]>([INPUTS_QUERY_KEY, orgId])
          ?.find((input) => input.id === request.source.inputId) ??
        lookups.source;
      onSourceRead(source);

      return copyPaddockData(request, {
        ...lookups,
        source,
        gUnits,
        userProfile,
        onSaved: (saved, target) => {
          if (isSameUser()) onSaved(saved, target);
        },
        onTargetDone,
        shouldContinue: isSameUser,
      });
    },
    [
      dispatch,
      gUnits,
      onSaved,
      orgId,
      queryClient,
      saveOpenPlan,
      store,
      userProfile,
    ]
  );

  return useCallback(
    (
      request: ICopyPaddockDataRequest,
      lookups: CopyLookups,
      labelOf: CopyTargetLabeller
    ): Promise<boolean> => {
      // Another copy got in first: nothing starts, so the tool stays open showing why.
      if (getIsCopyRunning(store.getState())) return Promise.resolve(false);
      if (lastSaveErrorToastId !== null) {
        toast.dismiss(lastSaveErrorToastId);
        lastSaveErrorToastId = null;
      }
      const rows = new Map<ICopyTarget, CopyProgressRow>(
        request.destination.targets.map((target) => [
          target,
          { target, label: labelOf(target), status: CopyRowStatus.Pending },
        ])
      );
      const rowList = () => Array.from(rows.values());
      const setStatus = (target: ICopyTarget, status: CopyRowStatus) => {
        const row = rows.get(target);
        if (row) row.status = status;
      };
      const countCopied = () =>
        rowList().filter((row) => row.status === CopyRowStatus.Copied).length;
      const pendingTitle = () =>
        COPY_TOOL_COPY_PENDING_MESSAGE(countCopied(), rows.size);
      // Set when the user closes the toast, so the outcome comes back as a new one.
      let isClosed = false;
      const closeButton = renderCloseButton(() => {
        isClosed = true;
      });
      // Closable while copying, so a stalled copy doesn't pin it; the copy carries on.
      let toastId = toast.loading(
        renderCopyProgress(pendingTitle(), rowList()),
        { closeButton }
      );
      const showProgress = () =>
        toast.update(toastId, {
          render: renderCopyProgress(pendingTitle(), rowList()),
          type: 'default',
          isLoading: true,
          autoClose: false,
          closeButton,
          closeOnClick: false,
        });
      let isFirstRun = true;
      // The source as the first run read it, so a Retry copies the same data to the rest.
      let pinnedSource: IInput | null = null;
      // The first run's open plan couldn't be saved; the tool stays open, so the toast offers no Retry.
      let isFirstSaveFailed = false;

      const run = async (
        targets: ICopyTarget[],
        onTargetDone?: (target: ICopyTarget) => void,
        onOpenPlanSaved?: () => void
      ) => {
        // A second click on Retry before the toast updates, or another copy still running.
        if (getIsCopyRunning(store.getState())) return;
        // Planning holds each existing document until its write lands, opened now or later.
        const copyingIds = new Set<string>();
        const finishCopying = (inputId: string | null) => {
          if (inputId === null || !copyingIds.delete(inputId)) return;
          dispatch(finishCopyingInput(inputId));
        };

        let failedTargets = targets;
        let openPlanSaveError: string | null = null;
        dispatch(copyStarted());
        try {
          targets.forEach((target) => {
            setStatus(target, CopyRowStatus.Pending);
            const existingId = getExistingTargetId(target, request.copyMode);
            if (existingId !== null) copyingIds.add(existingId);
          });
          dispatch(startCopyingInputs(Array.from(copyingIds)));
          showProgress();
          const result = await runCopy(
            {
              ...request,
              destination: { ...request.destination, targets },
            },
            lookups,
            (target, isCopied) => {
              setStatus(
                target,
                isCopied ? CopyRowStatus.Copied : CopyRowStatus.Failed
              );
              showProgress();
              finishCopying(getExistingTargetId(target, request.copyMode));
              onTargetDone?.(target);
            },
            () => onOpenPlanSaved?.(),
            pinnedSource,
            (source) => {
              pinnedSource = source;
            }
          );
          // The copy's own result says what saved, whatever its follow-ups did.
          ({ failedTargets } = result);
        } catch (error) {
          // Any other error is reported as the copy error, with the destinations not yet saved.
          if (error instanceof CopyOpenPlanSaveError) {
            openPlanSaveError = error.message;
          }
          failedTargets = targets.filter(
            (target) => rows.get(target)?.status !== CopyRowStatus.Copied
          );
        } finally {
          dispatch(copyFinished());
          Array.from(copyingIds).forEach(finishCopying);
        }
        const failed = new Set(failedTargets);
        targets.forEach((target) =>
          setStatus(
            target,
            failed.has(target) ? CopyRowStatus.Failed : CopyRowStatus.Copied
          )
        );

        const copiedCount = countCopied();
        const isDone = failedTargets.length === 0;
        let title: string;
        if (openPlanSaveError !== null) {
          title = COPY_TOOL_OPEN_PLAN_SAVE_ERROR_MESSAGE(openPlanSaveError);
        } else if (isDone) {
          title = COPY_TOOL_COPY_SUCCESS_MESSAGE(rows.size);
        } else if (copiedCount > 0) {
          title = COPY_TOOL_COPY_PARTIAL_ERROR_MESSAGE(
            copiedCount,
            rows.size,
            rowList()
              .filter((row) => row.status === CopyRowStatus.Failed)
              .map((row) => row.label)
              .join(', ')
          );
        } else {
          title = COPY_TOOL_COPY_ERROR_MESSAGE;
        }
        if (isFirstRun && openPlanSaveError !== null) isFirstSaveFailed = true;
        isFirstRun = false;
        const content = renderCopyProgress(
          title,
          rowList(),
          isDone || isFirstSaveFailed ? undefined : () => safeRun(failedTargets)
        );
        // Set here rather than through the toast wrapper: an error stays until it's dismissed.
        const options = {
          type: isDone ? ('success' as const) : ('error' as const),
          isLoading: false,
          autoClose: isDone ? COPY_TOOL_COPIED_FEEDBACK_MS : (false as const),
          closeButton,
          closeOnClick: isDone,
        };
        // Closed while copying, so the outcome and its Retry come back as a new toast.
        if (!isClosed && toast.isActive(toastId)) {
          toast.update(toastId, { ...options, render: content });
        } else {
          isClosed = false;
          toastId = toast(content, options);
        }
        if (isFirstSaveFailed) lastSaveErrorToastId = toastId;
      };

      // A throw while showing the outcome would leave the toast spinning; the copy error replaces it.
      const safeRun = (
        targets: ICopyTarget[],
        onTargetDone?: (target: ICopyTarget) => void,
        onOpenPlanSaved?: () => void
      ) =>
        run(targets, onTargetDone, onOpenPlanSaved).catch(() => {
          toast.dismiss(toastId);
          toast.error(COPY_TOOL_COPY_ERROR_MESSAGE);
        });

      const openPlanId = getSelectedInputId(store.getState());
      const openPlanTarget = openPlanId
        ? request.destination.targets.find(
            (target) =>
              getExistingTargetId(target, request.copyMode) === openPlanId
          )
        : undefined;
      return new Promise<boolean>((resolve) => {
        // First, so the tool waits on the open plan's save alone.
        const targets = openPlanTarget
          ? [
              openPlanTarget,
              ...request.destination.targets.filter(
                (target) => target !== openPlanTarget
              ),
            ]
          : request.destination.targets;
        // Held through the open plan's save, and its own write when it's a destination.
        safeRun(
          targets,
          (target) => {
            if (target === openPlanTarget) resolve(true);
          },
          () => {
            if (!openPlanTarget) resolve(true);
          }
        ).finally(() => resolve(!isFirstSaveFailed));
      });
    },
    [dispatch, runCopy, store]
  );
};
